import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getUser } from '../../utils/secureStorage';

/**
 * OnboardingBirthYear - Screen 2 of chapters onboarding
 *
 * Collects birth year (required) and hometown (optional).
 * Large, senior-friendly year selector with arrow buttons.
 */
function OnboardingBirthYear({ onNext, onBack }) {
  const currentYear = new Date().getFullYear();

  // Pre-fill birth year from user's date_of_birth if available
  const getInitialBirthYear = () => {
    try {
      const userData = getUser();
      const dob = userData?.date_of_birth;
      if (dob) {
        const year = parseInt(dob.substring(0, 4), 10);
        if (!isNaN(year) && year >= 1920 && year <= currentYear - 10) {
          return year;
        }
      }
    } catch { /* ignore */ }
    return 1990;
  };

  const [birthYear, setBirthYear] = useState(getInitialBirthYear);
  const [hometown, setHometown] = useState('');

  const minYear = 1920;
  const maxYear = currentYear - 10;

  // Handle year change with arrow buttons
  const incrementYear = () => {
    setBirthYear(prev => Math.min(prev + 1, maxYear));
  };

  const decrementYear = () => {
    setBirthYear(prev => Math.max(prev - 1, minYear));
  };

  // Handle submit
  const handleSubmit = () => {
    onNext({ birthYear, hometown: hometown.trim() });
  };

  return (
    <div className="min-h-screen bg-fb-cream flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {/* Header */}
        <h1 className="font-serif text-2xl font-bold text-gray-900 mb-8 text-center">
          Just two quick things...
        </h1>

        {/* Birth Year Selector */}
        <div className="mb-8">
          <label className="block font-serif text-lg text-gray-800 mb-4 text-center">
            What year were you born?
          </label>

          <div className="flex items-center justify-center gap-4">
            {/* Decrement Button */}
            <button
              onClick={decrementYear}
              disabled={birthYear <= minYear}
              className="
                w-14 h-14 rounded-xl
                bg-fb-olive text-white text-2xl font-bold
                hover:bg-fb-olive/90 disabled:opacity-40 disabled:cursor-not-allowed
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-fb-olive focus:ring-offset-2
                flex items-center justify-center
              "
              aria-label="Previous year"
            >
              ◂
            </button>

            {/* Year Input */}
            <input
              type="number"
              value={birthYear}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setBirthYear('');
                } else {
                  const val = parseInt(raw, 10);
                  if (!isNaN(val)) {
                    setBirthYear(val);
                  }
                }
              }}
              onBlur={() => {
                if (birthYear === '' || birthYear < minYear) setBirthYear(minYear);
                if (birthYear > maxYear) setBirthYear(maxYear);
              }}
              min={minYear}
              max={maxYear}
              className="
                w-40 h-14 rounded-xl
                bg-white border-2 border-fb-olive
                text-center
                font-serif text-3xl font-bold text-gray-900
                focus:outline-none focus:ring-2 focus:ring-fb-olive
                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
              "
            />

            {/* Increment Button */}
            <button
              onClick={incrementYear}
              disabled={birthYear >= maxYear}
              className="
                w-14 h-14 rounded-xl
                bg-fb-olive text-white text-2xl font-bold
                hover:bg-fb-olive/90 disabled:opacity-40 disabled:cursor-not-allowed
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-fb-olive focus:ring-offset-2
                flex items-center justify-center
              "
              aria-label="Next year"
            >
              ▸
            </button>
          </div>
        </div>

        {/* Hometown Field (Optional) */}
        <div className="mb-8">
          <label className="block font-serif text-lg text-gray-800 mb-2">
            Where did you grow up?
            <span className="text-gray-500 text-base ml-2">(optional)</span>
          </label>

          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">
              📍
            </span>
            <input
              type="text"
              value={hometown}
              onChange={(e) => setHometown(e.target.value)}
              placeholder="City, State, Country"
              className="
                w-full pl-12 pr-4 py-4
                rounded-xl border border-gray-300
                font-sans text-base text-gray-900
                focus:outline-none focus:ring-2 focus:ring-fb-olive focus:border-fb-olive
                placeholder:text-gray-400
              "
            />
          </div>
        </div>

        {/* Confirmation Message */}
        <div className="flex items-center justify-center gap-2 mb-8 text-fb-olive">
          <span className="text-xl">✅</span>
          <span className="font-serif text-base">
            That's all we need to set up your story!
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleSubmit}
            className="
              w-full bg-fb-olive hover:bg-fb-olive/90 text-white
              font-bold text-lg py-4 px-6
              rounded-xl shadow-md
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-fb-olive focus:ring-offset-2
            "
          >
            Create My Story →
          </button>

          <button
            onClick={onBack}
            className="
              w-full bg-transparent text-gray-600
              font-sans text-base py-2
              rounded-xl
              hover:text-gray-900
              transition-colors duration-200
            "
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
}

OnboardingBirthYear.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default OnboardingBirthYear;
